import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { TabView, TabPanel } from 'primereact/tabview';
import { Panel } from 'primereact/panel';
import { Divider } from 'primereact/divider';
import { InputNumber } from 'primereact/inputnumber';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { ImageSlotGrid } from '../../../../components/common/ImageSlotGrid';
import { Driver } from '../../../../../types/cms.types';
import { FileUploadService } from '../../../../../service/supabase/FileUploadService';

interface CpnCreateEditDriverProps {
    visible: boolean;
    onHide: () => void;
    driver: Driver | null;
    onSave: (driverData: any) => void;
}

export const CpnCreateEditDriver: React.FC<CpnCreateEditDriverProps> = ({
    visible,
    onHide,
    driver,
    onSave
}) => {
    const [formData, setFormData] = useState({
        // Basic info
        full_name: '',
        phone_number: '',
        email: '',
        pass_word: '',
        date_of_birth: null as Date | null,
        sex: '',
        vehicle_type: 'motorbike' as 'car' | 'motorbike',
        status: 'pending' as 'pending' | 'approved' | 'rejected',
        city_of_operation: '',
        url_avatar: '',

        // ID info - CCCD
        cccd_number: '',
        cccd_issue_date: null as Date | null,
        cccd_issue_place: '',
        cccd_images: [] as string[],

        // Address
        permanent_address: '',
        temporary_address: '',
        current_job: '',

        // Bank info
        bank_name: '',
        account_holder_name: '',
        account_number: '',

        // Tax info
        tax_id: '',
        tax_issue_place: '',

        // Emergency contact
        emergency_contact_name: '',
        emergency_contact_phone: '',
        emergency_contact_relationship: '',

        // Motorcycle info
        motorcycle_plate_number: '',
        motorcycle_brand: '',
        motorcycle_model: '',
        motorcycle_year: null as number | null,
        motorcycle_color: '',
        motorcycle_owner_name: '',

        // Motorcycle license
        motorcycle_license_number: '',
        motorcycle_license_serial: '',
        motorcycle_license_class: '',
        motorcycle_license_issue_date: null as Date | null,

        // Car info
        car_plate_number: '',
        car_brand: '',
        car_model: '',
        car_year: null as number | null,
        car_color: '',
        car_seats: null as number | null,
        car_fuel_type: '',
        car_owner_name: '',

        // Car license
        car_license_number: '',
        car_license_serial: '',
        car_license_class: '',
        car_license_selection_date: null as Date | null,
        car_license_expiry_date: null as Date | null,
        expiry_date: null as Date | null,

        // Documents URLs (using exact Driver interface field names)
        motorcycle_license_url: [] as string[],
        motorcycle_front_image: '',
        motorcycle_rear_image: '',
        car_license_url: [] as string[],
        car_front_image: '',
        car_rear_image: '',
        car_front_seat_image: '',
        car_rear_seat_image: '',
        residence_confirmation_url: [] as string[],
        vehicle_registration_urls: [] as string[],
        inspection_certificate_url: [] as string[],
        vehicle_badge_url: [] as string[],
        reflective_sticker_url: '',
        insurance_certificate_url: [] as string[],
        health_certificate_urls: [] as string[],
        drug_test_urls: [] as string[],
        criminal_record_urls: [] as string[],
        commitment_video_url: '',
        cooperative_member_urls: [] as string[],
        signature_url: '',


    });

    const [activeIndex, setActiveIndex] = useState(0);
    const [uploadingFiles, setUploadingFiles] = useState<{ [key: string]: boolean }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
    const toast = useRef<Toast>(null);

    // Image slot handlers for single images
    const handleImageAdd = async (fieldName: string, index: number, file: File) => {
        try {
            // Set loading state for this specific field and index
            const loadingKey = `${fieldName}_${index}`;
            setUploadingFiles(prev => ({ ...prev, [loadingKey]: true }));
            setUploadProgress(prev => ({ ...prev, [loadingKey]: 0 }));
            setIsLoading(true);

            // Simulate progress for better UX
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    const current = prev[loadingKey] || 0;
                    if (current < 80) {
                        return { ...prev, [loadingKey]: current + 10 };
                    }
                    return prev;
                });
            }, 200);

            // Get phone number for folder organization
            const phoneNumber = formData.phone_number || 'temp';

            // Upload file to Supabase Storage
            const fileUrl = await FileUploadService.uploadDriverDocument(
                file,
                phoneNumber,
                fieldName
            );

            if (!fileUrl) {
                console.error('Failed to upload file');
                clearInterval(progressInterval);
                return;
            }

            // Complete the progress
            setUploadProgress(prev => ({ ...prev, [loadingKey]: 100 }));
            clearInterval(progressInterval);

            const currentValue = formData[fieldName as keyof typeof formData];

            if (Array.isArray(currentValue)) {
                const currentFiles = [...currentValue];
                // Ensure array has enough slots
                while (currentFiles.length <= index) {
                    currentFiles.push('');
                }
                currentFiles[index] = fileUrl;
                setFormData(prev => ({
                    ...prev,
                    [fieldName]: currentFiles
                }));
            } else {
                // For single image fields
                setFormData(prev => ({
                    ...prev,
                    [fieldName]: fileUrl
                }));
            }

            // Show success notification
            toast.current?.show({
                severity: 'success',
                summary: 'Thành công',
                detail: 'Tải file lên thành công!',
                life: 3000
            });
        } catch (error) {
            console.error('Error uploading file:', error);
            // Show error notification
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: 'Có lỗi xảy ra khi tải file lên. Vui lòng thử lại.',
                life: 5000
            });
        } finally {
            // Clear loading state and progress
            const loadingKey = `${fieldName}_${index}`;
            setUploadingFiles(prev => {
                const newState = { ...prev };
                delete newState[loadingKey];
                return newState;
            });
            setUploadProgress(prev => {
                const newState = { ...prev };
                delete newState[loadingKey];
                return newState;
            });
            setIsLoading(false);
        }
    };

    const handleImageRemove = async (fieldName: string, index: number) => {
        try {
            // Set loading state
            const loadingKey = `${fieldName}_${index}_remove`;
            setUploadingFiles(prev => ({ ...prev, [loadingKey]: true }));
            setIsLoading(true);

            const currentValue = formData[fieldName as keyof typeof formData];

            if (Array.isArray(currentValue)) {
                const currentFiles = [...currentValue];
                if (currentFiles[index]) {
                    // Delete file from storage
                    await FileUploadService.deleteFile(currentFiles[index]);

                    currentFiles[index] = '';
                    setFormData(prev => ({
                        ...prev,
                        [fieldName]: currentFiles
                    }));
                }
            } else {
                // For single image fields
                const fileUrl = currentValue as string;
                if (fileUrl) {
                    // Delete file from storage
                    await FileUploadService.deleteFile(fileUrl);
                }

                setFormData(prev => ({
                    ...prev,
                    [fieldName]: ''
                }));
            }

            console.log('File removed successfully');
            toast.current?.show({
                severity: 'info',
                summary: 'Đã xóa',
                detail: 'File đã được xóa thành công!',
                life: 3000
            });
        } catch (error) {
            console.error('Error deleting file from storage:', error);
            // Show error notification
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: 'Có lỗi xảy ra khi xóa file. Vui lòng thử lại.',
                life: 5000
            });
        } finally {
            // Clear loading state
            const loadingKey = `${fieldName}_${index}_remove`;
            setUploadingFiles(prev => {
                const newState = { ...prev };
                delete newState[loadingKey];
                return newState;
            });
            setIsLoading(false);
        }
    };

    // Helper function to check if a field is currently uploading
    const isFieldUploading = (fieldName: string, index?: number) => {
        const loadingKey = index !== undefined ? `${fieldName}_${index}` : fieldName;
        const removeKey = index !== undefined ? `${fieldName}_${index}_remove` : `${fieldName}_remove`;
        return uploadingFiles[loadingKey] || uploadingFiles[removeKey] || false;
    };

    // Options
    const vehicleTypeOptions = [
        { label: 'Xe máy', value: 'motorbike' },
        { label: 'Ô tô', value: 'car' }
    ];

    const statusOptions = [
        { label: 'Chờ duyệt', value: 'pending' },
        { label: 'Đã kích hoạt', value: 'approved' },
        { label: 'Đã khóa', value: 'rejected' }
    ];

    const sexOptions = [
        { label: 'Nam', value: 'Male' },
        { label: 'Nữ', value: 'Female' },
        { label: 'Khác', value: 'Other' }
    ];

    const cityOptions = [
        { label: 'Tất cả thành phố', value: '' },
        { label: 'An Giang', value: 'An Giang' },
        { label: 'Bắc Ninh', value: 'Bắc Ninh' },
        { label: 'Cà Mau', value: 'Cà Mau' },
        { label: 'Cần Thơ', value: 'Cần Thơ' },
        { label: 'Cao Bằng', value: 'Cao Bằng' },
        { label: 'Đà Nẵng', value: 'Đà Nẵng' },
        { label: 'Đắk Lắk', value: 'Đắk Lắk' },
        { label: 'Điện Biên', value: 'Điện Biên' },
        { label: 'Đồng Nai', value: 'Đồng Nai' },
        { label: 'Đồng Tháp', value: 'Đồng Tháp' },
        { label: 'Gia Lai', value: 'Gia Lai' },
        { label: 'Hà Nội', value: 'Hà Nội' },
        { label: 'Hà Tĩnh', value: 'Hà Tĩnh' },
        { label: 'Hải Phòng', value: 'Hải Phòng' },
        { label: 'TP.HCM', value: 'Hồ Chí Minh' },
        { label: 'Huế', value: 'Huế' },
        { label: 'Hưng Yên', value: 'Hưng Yên' },
        { label: 'Khánh Hoà', value: 'Khánh Hoà' },
        { label: 'Lai Châu', value: 'Lai Châu' },
        { label: 'Lâm Đồng', value: 'Lâm Đồng' },
        { label: 'Lạng Sơn', value: 'Lạng Sơn' },
        { label: 'Lào Cai', value: 'Lào Cai' },
        { label: 'Nghệ An', value: 'Nghệ An' },
        { label: 'Ninh Bình', value: 'Ninh Bình' },
        { label: 'Phú Thọ', value: 'Phú Thọ' },
        { label: 'Quảng Ngãi', value: 'Quảng Ngãi' },
        { label: 'Quảng Ninh', value: 'Quảng Ninh' },
        { label: 'Quảng Trị', value: 'Quảng Trị' },
        { label: 'Sơn La', value: 'Sơn La' },
        { label: 'Tây Ninh', value: 'Tây Ninh' },
        { label: 'Thái Nguyên', value: 'Thái Nguyên' },
        { label: 'Thanh Hoá', value: 'Thanh Hoá' },
        { label: 'Tuyên Quang', value: 'Tuyên Quang' },
        { label: 'Vĩnh Long', value: 'Vĩnh Long' }
    ];

    const licenseClassOptions = [
        { label: 'A1', value: 'A1' },
        { label: 'A2', value: 'A2' },
        { label: 'B1', value: 'B1' },
        { label: 'B', value: 'B' },
        { label: 'B2', value: 'B2' },
        { label: 'C', value: 'C' },
        { label: 'D', value: 'D' },
        { label: 'E', value: 'E' },
        { label: 'F', value: 'F' }
    ];

    const fuelTypeOptions = [
        { label: 'Xăng', value: 'Xăng' },
        { label: 'Diesel', value: 'Diesel' },
        { label: 'Điện', value: 'Điện' },
        { label: 'Hybrid', value: 'Hybrid' }
    ];

    const relationshipOptions = [
        { label: 'Bố', value: 'Bố' },
        { label: 'Mẹ', value: 'Mẹ' },
        { label: 'Vợ/Chồng', value: 'Vợ/Chồng' },
        { label: 'Con', value: 'Con' },
        { label: 'Anh/Em', value: 'Anh/Em' },
        { label: 'Bạn', value: 'Bạn' },
        { label: 'Khác', value: 'Khác' }
    ];

    // Load driver data when dialog opens
    useEffect(() => {
        if (visible && driver) {
            setFormData({
                full_name: driver.full_name || '',
                phone_number: driver.phone_number || '',
                email: driver.email || '',
                pass_word: driver.pass_word || '',
                date_of_birth: driver.date_of_birth ? new Date(driver.date_of_birth) : null,
                sex: driver.sex || '',
                vehicle_type: driver.vehicle_type || 'motorbike',
                status: driver.status || 'pending',
                city_of_operation: driver.city_of_operation || '',
                url_avatar: driver.url_avatar || '',

                cccd_number: driver.cccd_number || '',
                cccd_issue_date: driver.cccd_issue_date ? new Date(driver.cccd_issue_date) : null,
                cccd_issue_place: driver.cccd_issue_place || '',
                cccd_images: driver.cccd_images || [],

                permanent_address: driver.permanent_address || '',
                temporary_address: driver.temporary_address || '',
                current_job: driver.current_job || '',

                bank_name: driver.bank_name || '',
                account_holder_name: driver.account_holder_name || '',
                account_number: driver.account_number || '',

                tax_id: driver.tax_id || '',
                tax_issue_place: driver.tax_issue_place || '',

                emergency_contact_name: driver.emergency_contact_name || '',
                emergency_contact_phone: driver.emergency_contact_phone || '',
                emergency_contact_relationship: driver.emergency_contact_relationship || '',

                motorcycle_plate_number: driver.motorcycle_plate_number || '',
                motorcycle_brand: driver.motorcycle_brand || '',
                motorcycle_model: driver.motorcycle_model || '',
                motorcycle_year: driver.motorcycle_year || null,
                motorcycle_color: driver.motorcycle_color || '',
                motorcycle_owner_name: driver.motorcycle_owner_name || '',

                motorcycle_license_number: driver.motorcycle_license_number || '',
                motorcycle_license_serial: driver.motorcycle_license_serial || '',
                motorcycle_license_class: driver.motorcycle_license_class || '',
                motorcycle_license_issue_date: driver.motorcycle_license_issue_date ? new Date(driver.motorcycle_license_issue_date) : null,

                car_plate_number: driver.car_plate_number || '',
                car_brand: driver.car_brand || '',
                car_model: driver.car_model || '',
                car_year: driver.car_year || null,
                car_color: driver.car_color || '',
                car_seats: driver.car_seats || null,
                car_fuel_type: driver.car_fuel_type || '',
                car_owner_name: driver.car_owner_name || '',

                car_license_number: driver.car_license_number || '',
                car_license_serial: driver.car_license_serial || '',
                car_license_class: driver.car_license_class || '',
                car_license_selection_date: driver.car_license_selection_date ? new Date(driver.car_license_selection_date) : null,
                car_license_expiry_date: driver.car_license_expiry_date ? new Date(driver.car_license_expiry_date) : null,
                expiry_date: driver.expiry_date ? new Date(driver.expiry_date) : null,

                motorcycle_license_url: driver.motorcycle_license_url || [],
                motorcycle_front_image: driver.motorcycle_front_image || '',
                motorcycle_rear_image: driver.motorcycle_rear_image || '',
                car_license_url: driver.car_license_url || [],
                car_front_image: driver.car_front_image || '',
                car_rear_image: driver.car_rear_image || '',
                car_front_seat_image: driver.car_front_seat_image || '',
                car_rear_seat_image: driver.car_rear_seat_image || '',
                residence_confirmation_url: driver.residence_confirmation_url || [],
                vehicle_registration_urls: driver.vehicle_registration_urls || [],
                inspection_certificate_url: driver.inspection_certificate_url || [],
                vehicle_badge_url: driver.vehicle_badge_url || [],
                reflective_sticker_url: driver.reflective_sticker_url || '',
                insurance_certificate_url: driver.insurance_certificate_url || [],
                health_certificate_urls: driver.health_certificate_urls || [],
                drug_test_urls: driver.drug_test_urls || [],
                criminal_record_urls: driver.criminal_record_urls || [],
                commitment_video_url: driver.commitment_video_url || '',
                cooperative_member_urls: driver.cooperative_member_urls || [],
                signature_url: driver.signature_url || '',

                // driver_code_now: driver.driver_code_now || ''
            });
        }
    }, [visible, driver]);

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleClose = () => {
        if (!isSaving) {
            onHide();
        }
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);

            // Show loading notification
            toast.current?.show({
                severity: 'info',
                summary: 'Đang xử lý',
                detail: 'Đang cập nhật thông tin tài xế...',
                life: 2000
            });

            // Call the parent save function
            await onSave(formData);

            // Show success notification
            toast.current?.show({
                severity: 'success',
                summary: 'Thành công',
                detail: 'Cập nhật thông tin tài xế thành công!',
                life: 3000
            });
        } catch (error) {
            console.error('Error saving driver data:', error);
            // Show error notification
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: 'Có lỗi xảy ra khi cập nhật thông tin. Vui lòng thử lại.',
                life: 5000
            });
        } finally {
            setIsSaving(false);
        }
    };

    const isFormValid = () => {
        const required = ['full_name', 'phone_number'];

        if (formData.vehicle_type === 'car') {
            required.push('car_plate_number');
        } else {
            required.push('motorcycle_plate_number');
        }

        return required.every(field => {
            const value = formData[field as keyof typeof formData];
            return value !== '' && value !== null && value !== undefined;
        });
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                onHide={handleClose}
                header={`Chỉnh sửa thông tin tài xế: ${driver?.full_name || 'Không xác định'}`}
                style={{ width: '90vw', maxWidth: '1200px', position: 'relative' }}
                maximizable
                modal
                closable={!isSaving}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button
                            label="Hủy"
                            icon="pi pi-times"
                            onClick={handleClose}
                            className="p-button-text"
                            disabled={isLoading || isSaving}
                        />
                        <Button
                            label={isSaving ? "Đang lưu..." : "Lưu thay đổi"}
                            icon={isSaving ? "pi pi-spin pi-spinner" : "pi pi-check"}
                            onClick={handleSave}
                            disabled={!isFormValid() || isLoading || isSaving}
                            className="p-button-success"
                            loading={isSaving}
                        />
                    </div>
                }
            >
                <div className="relative">

                    <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                        {/* Basic Information Tab */}
                        <TabPanel header="Thông tin cơ bản" leftIcon="pi pi-user">
                            <Panel header="Thông tin cá nhân" className="mb-4" toggleable>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">
                                            Họ và tên <span className="text-red-500">*</span>
                                        </label>
                                        <InputText
                                            value={formData.full_name}
                                            onChange={(e) => handleInputChange('full_name', e.target.value)}
                                            className="w-full"
                                            placeholder="Nhập họ và tên"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">
                                            Số điện thoại <span className="text-red-500">*</span>
                                        </label>
                                        <InputText
                                            value={formData.phone_number}
                                            onChange={(e) => handleInputChange('phone_number', e.target.value)}
                                            className="w-full"
                                            placeholder="Nhập số điện thoại"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Email</label>
                                        <InputText
                                            value={formData.email}
                                            onChange={(e) => handleInputChange('email', e.target.value)}
                                            className="w-full"
                                            placeholder="Nhập email"
                                            type="email"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Ngày sinh</label>
                                        <Calendar
                                            value={formData.date_of_birth}
                                            onChange={(e) => handleInputChange('date_of_birth', e.value)}
                                            className="w-full"
                                            placeholder="Chọn ngày sinh"
                                            dateFormat="dd/mm/yy"
                                            showIcon
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Giới tính</label>
                                        <Dropdown
                                            value={formData.sex}
                                            options={sexOptions}
                                            onChange={(e) => handleInputChange('sex', e.value)}
                                            className="w-full"
                                            placeholder="Chọn giới tính"
                                        />
                                    </div>

                                    {/* <div>
                                        <label className="block text-sm font-medium mb-1">Nghề nghiệp hiện tại</label>
                                        <InputText
                                            value={formData.current_job}
                                            onChange={(e) => handleInputChange('current_job', e.target.value)}
                                            className="w-full"
                                            placeholder="Nhập nghề nghiệp"
                                        />
                                    </div> */}
                                </div>
                            </Panel>

                            <Panel header="Thông tin phương tiện và hoạt động" className="mb-4" toggleable>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">
                                            Loại phương tiện <span className="text-red-500">*</span>
                                        </label>
                                        <Dropdown
                                            value={formData.vehicle_type}
                                            options={vehicleTypeOptions}
                                            onChange={(e) => handleInputChange('vehicle_type', e.value)}
                                            className="w-full"
                                            placeholder="Chọn loại phương tiện"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Trạng thái</label>
                                        <Dropdown
                                            value={formData.status}
                                            options={statusOptions}
                                            onChange={(e) => handleInputChange('status', e.value)}
                                            className="w-full"
                                            placeholder="Chọn trạng thái"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Thành phố hoạt động</label>
                                        <Dropdown
                                            value={formData.city_of_operation}
                                            options={cityOptions}
                                            onChange={(e) => handleInputChange('city_of_operation', e.value)}
                                            className="w-full"
                                            placeholder="Chọn thành phố"
                                            editable
                                        />
                                    </div>
                                </div>
                            </Panel>

                            <Panel header="Ảnh đại diện" className="mb-4" toggleable>
                                <div className="relative">
                                    <ImageSlotGrid
                                        label="Ảnh đại diện"
                                        images={formData.url_avatar ? [formData.url_avatar] : []}
                                        maxImages={1}
                                        onImageAdd={(index, file) => handleImageAdd('url_avatar', index, file)}
                                        onImageRemove={(index) => handleImageRemove('url_avatar', index)}
                                        accept="image/*"
                                        slotWidth="200px"
                                        slotHeight="200px"
                                        description="1 ảnh đại diện"
                                        disabled={isFieldUploading('url_avatar', 0)}
                                    />
                                    {isFieldUploading('url_avatar', 0) && (
                                        <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-lg">
                                            <div className="flex flex-col items-center space-y-3">
                                                <i className="pi pi-spin pi-spinner text-blue-500 text-xl"></i>
                                                <span className="text-sm text-gray-600">Đang tải ảnh...</span>
                                                <ProgressBar
                                                    value={uploadProgress['url_avatar_0'] || 0}
                                                    className="w-32"
                                                    showValue={false}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Panel>
                        </TabPanel>

                        {/* Identity Information Tab */}
                        <TabPanel header="Giấy tờ tùy thân" leftIcon="pi pi-id-card">
                            <Panel header="Thông tin CCCD" className="mb-4" toggleable>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Số CCCD</label>
                                        <InputText
                                            value={formData.cccd_number}
                                            onChange={(e) => handleInputChange('cccd_number', e.target.value)}
                                            className="w-full"
                                            placeholder="Nhập số CCCD"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Ngày cấp CCCD</label>
                                        <Calendar
                                            value={formData.cccd_issue_date}
                                            onChange={(e) => handleInputChange('cccd_issue_date', e.value)}
                                            className="w-full"
                                            placeholder="Chọn ngày cấp"
                                            dateFormat="dd/mm/yy"
                                            showIcon
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium mb-1">Nơi cấp CCCD</label>
                                        <InputText
                                            value={formData.cccd_issue_place}
                                            onChange={(e) => handleInputChange('cccd_issue_place', e.target.value)}
                                            className="w-full"
                                            placeholder="Nhập nơi cấp CCCD"
                                        />
                                    </div>
                                </div>
                            </Panel>

                            <Panel header="Hình ảnh CCCD & Xác nhận nơi cư trú" className="mb-4" toggleable>
                                <div className="relative -mb-4">
                                    <ImageSlotGrid
                                        label="Hình ảnh CCCD"
                                        images={formData.cccd_images || []}
                                        maxImages={2}
                                        onImageAdd={(index, file) => handleImageAdd('cccd_images', index, file)}
                                        onImageRemove={(index) => handleImageRemove('cccd_images', index)}
                                        accept="image/*,application/pdf"
                                        required
                                        description="Tối đa 2 ảnh CCCD (mặt trước và mặt sau), tối thiểu 1 ảnh"
                                        disabled={isFieldUploading('cccd_images')}
                                    />
                                    {(isFieldUploading('cccd_images', 0) || isFieldUploading('cccd_images', 1)) && (
                                        <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-lg">
                                            <div className="flex flex-col items-center space-y-3">
                                                <i className="pi pi-spin pi-spinner text-blue-500 text-xl"></i>
                                                <span className="text-sm text-gray-600">Đang tải ảnh CCCD...</span>
                                                <ProgressBar
                                                    value={Math.max(
                                                        uploadProgress['cccd_images_0'] || 0,
                                                        uploadProgress['cccd_images_1'] || 0
                                                    )}
                                                    className="w-32"
                                                    showValue={false}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>


                            </Panel>

                            <Panel header="Địa chỉ" className="mb-2" toggleable>
                                <div className="grid grid-cols-1 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Địa chỉ thường trú</label>
                                        <InputText
                                            value={formData.permanent_address}
                                            onChange={(e) => handleInputChange('permanent_address', e.target.value)}
                                            className="w-full"
                                            placeholder="Nhập địa chỉ thường trú"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Địa chỉ tạm trú</label>
                                        <InputText
                                            value={formData.temporary_address}
                                            onChange={(e) => handleInputChange('temporary_address', e.target.value)}
                                            className="w-full"
                                            placeholder="Nhập địa chỉ tạm trú"
                                        />
                                    </div>

                                </div>
                                <div className="relative mt-3 -mb-4 ">
                                    <ImageSlotGrid
                                        label="Xác nhận nơi cư trú"
                                        images={formData.residence_confirmation_url || []}
                                        maxImages={2}
                                        onImageAdd={(index, file) => handleImageAdd('residence_confirmation_url', index, file)}
                                        onImageRemove={(index) => handleImageRemove('residence_confirmation_url', index)}
                                        accept="image/*,application/pdf"
                                        required
                                        description="Tối đa 2 ảnh, tối thiểu 1 ảnh"
                                        disabled={isFieldUploading('residence_confirmation_url')}
                                    />
                                    {(isFieldUploading('residence_confirmation_url', 0) || isFieldUploading('residence_confirmation_url', 1)) && (
                                        <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-lg">
                                            <div className="flex flex-col items-center space-y-3">
                                                <i className="pi pi-spin pi-spinner text-blue-500 text-xl"></i>
                                                <span className="text-sm text-gray-600">Đang tải giấy tờ...</span>
                                                <ProgressBar
                                                    value={Math.max(
                                                        uploadProgress['residence_confirmation_url_0'] || 0,
                                                        uploadProgress['residence_confirmation_url_1'] || 0
                                                    )}
                                                    className="w-32"
                                                    showValue={false}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Panel>
                        </TabPanel>

                        {/* Vehicle Information Tab */}
                        <TabPanel header="Thông tin phương tiện" leftIcon="pi pi-car">
                            {formData.vehicle_type === 'motorbike' ? (
                                <>
                                    <Panel header="Thông tin xe máy" className="mb-4" toggleable>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium mb-1">
                                                    Biển số xe <span className="text-red-500">*</span>
                                                </label>
                                                <InputText
                                                    value={formData.motorcycle_plate_number}
                                                    onChange={(e) => handleInputChange('motorcycle_plate_number', e.target.value)}
                                                    className="w-full"
                                                    placeholder="Nhập biển số xe máy"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-1">Hãng xe</label>
                                                <InputText
                                                    value={formData.motorcycle_brand}
                                                    onChange={(e) => handleInputChange('motorcycle_brand', e.target.value)}
                                                    className="w-full"
                                                    placeholder="Nhập hãng xe"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-1">Mẫu xe</label>
                                                <InputText
                                                    value={formData.motorcycle_model}
                                                    onChange={(e) => handleInputChange('motorcycle_model', e.target.value)}
                                                    className="w-full"
                                                    placeholder="Nhập mẫu xe"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-1">Năm sản xuất</label>
                                                <InputNumber
                                                    value={formData.motorcycle_year}
                                                    onValueChange={(e) => handleInputChange('motorcycle_year', e.value)}
                                                    className="w-full"

                                                    placeholder="Nhập năm sản xuất"
                                                    min={1990}
                                                    max={new Date().getFullYear()}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-1">Màu xe</label>
                                                <InputText
                                                    value={formData.motorcycle_color}
                                                    onChange={(e) => handleInputChange('motorcycle_color', e.target.value)}
                                                    className="w-full"
                                                    placeholder="Nhập màu xe"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-1">Tên chủ sở hữu</label>
                                                <InputText
                                                    value={formData.motorcycle_owner_name}
                                                    onChange={(e) => handleInputChange('motorcycle_owner_name', e.target.value)}
                                                    className="w-full"
                                                    placeholder="Nhập tên chủ sở hữu"
                                                />
                                            </div>
                                        </div>
                                    </Panel>

                                    <Panel header="Hình ảnh xe máy" className="mb-4" toggleable>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <ImageSlotGrid
                                                label="Ảnh phía trước xe máy"
                                                images={formData.motorcycle_front_image ? [formData.motorcycle_front_image] : []}
                                                maxImages={1}
                                                onImageAdd={(index, file) => handleImageAdd('motorcycle_front_image', index, file)}
                                                onImageRemove={(index) => handleImageRemove('motorcycle_front_image', index)}
                                                accept="image/*"
                                                required
                                                slotWidth="200px"
                                                slotHeight="150px"
                                            />

                                            <ImageSlotGrid
                                                label="Ảnh phía sau xe máy"
                                                images={formData.motorcycle_rear_image ? [formData.motorcycle_rear_image] : []}
                                                maxImages={1}
                                                onImageAdd={(index, file) => handleImageAdd('motorcycle_rear_image', index, file)}
                                                onImageRemove={(index) => handleImageRemove('motorcycle_rear_image', index)}
                                                accept="image/*"
                                                required
                                                slotWidth="200px"
                                                slotHeight="150px"
                                            />
                                        </div>
                                    </Panel>

                                    <Panel header="Giấy tờ phương tiện" className="mb-4" toggleable>
                                        {/* Nhóm giấy tờ chính */}
                                        <div className="mb-6">
                                            <h5 className="text-md font-medium mb-3 text-gray-700">Giấy tờ đăng ký và kiểm định</h5>
                                            <div className="grid grid-cols-1 gap-4">
                                                <ImageSlotGrid
                                                    label="Đăng ký xe / biên nhận thế chấp"
                                                    images={formData.vehicle_registration_urls || []}
                                                    maxImages={2}
                                                    onImageAdd={(index, file) => handleImageAdd('vehicle_registration_urls', index, file)}
                                                    onImageRemove={(index) => handleImageRemove('vehicle_registration_urls', index)}
                                                    accept="image/*,application/pdf"
                                                    required
                                                    description="Tối đa 2 ảnh, tối thiểu 1 ảnh"
                                                />


                                            </div>
                                        </div>


                                    </Panel>
                                </>
                            ) : (
                                <>
                                    <Panel header="Thông tin ô tô" className="mb-4" toggleable>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium mb-1">
                                                    Biển số xe <span className="text-red-500">*</span>
                                                </label>
                                                <InputText
                                                    value={formData.car_plate_number}
                                                    onChange={(e) => handleInputChange('car_plate_number', e.target.value)}
                                                    className="w-full"
                                                    placeholder="Nhập biển số ô tô"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-1">Hãng xe</label>
                                                <InputText
                                                    value={formData.car_brand}
                                                    onChange={(e) => handleInputChange('car_brand', e.target.value)}
                                                    className="w-full"
                                                    placeholder="Nhập hãng xe"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-1">Mẫu xe</label>
                                                <InputText
                                                    value={formData.car_model}
                                                    onChange={(e) => handleInputChange('car_model', e.target.value)}
                                                    className="w-full"
                                                    placeholder="Nhập mẫu xe"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-1">Năm sản xuất</label>
                                                <InputNumber
                                                    value={formData.car_year}
                                                    onValueChange={(e) => handleInputChange('car_year', e.value)}
                                                    className="w-full"
                                                    placeholder="Nhập năm sản xuất"
                                                    min={1990}
                                                    max={new Date().getFullYear()}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-1">Màu xe</label>
                                                <InputText
                                                    value={formData.car_color}
                                                    onChange={(e) => handleInputChange('car_color', e.target.value)}
                                                    className="w-full"
                                                    placeholder="Nhập màu xe"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-1">Số chỗ ngồi</label>
                                                <InputNumber
                                                    value={formData.car_seats}
                                                    onValueChange={(e) => handleInputChange('car_seats', e.value)}
                                                    className="w-full"
                                                    placeholder="Nhập số chỗ ngồi"
                                                    min={2}
                                                    max={50}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-1">Loại nhiên liệu</label>
                                                <Dropdown
                                                    value={formData.car_fuel_type}
                                                    options={fuelTypeOptions}
                                                    onChange={(e) => handleInputChange('car_fuel_type', e.value)}
                                                    className="w-full"
                                                    placeholder="Chọn loại nhiên liệu"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-1">Tên chủ sở hữu</label>
                                                <InputText
                                                    value={formData.car_owner_name}
                                                    onChange={(e) => handleInputChange('car_owner_name', e.target.value)}
                                                    className="w-full"
                                                    placeholder="Nhập tên chủ sở hữu"
                                                />
                                            </div>
                                        </div>
                                    </Panel>

                                    <Panel header="Hình ảnh ô tô" className="mb-4" toggleable>
                                        {/* Nhóm ảnh bên ngoài xe */}
                                        <div className="mb-6">
                                            <h5 className="text-md font-medium mb-3 text-gray-700">Ảnh bên ngoài xe</h5>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <ImageSlotGrid
                                                    label="Ảnh phía trước ô tô"
                                                    images={formData.car_front_image ? [formData.car_front_image] : []}
                                                    maxImages={1}
                                                    onImageAdd={(index, file) => handleImageAdd('car_front_image', index, file)}
                                                    onImageRemove={(index) => handleImageRemove('car_front_image', index)}
                                                    accept="image/*"
                                                    required
                                                    slotWidth="200px"
                                                    slotHeight="150px"
                                                />

                                                <ImageSlotGrid
                                                    label="Ảnh phía sau ô tô"
                                                    images={formData.car_rear_image ? [formData.car_rear_image] : []}
                                                    maxImages={1}
                                                    onImageAdd={(index, file) => handleImageAdd('car_rear_image', index, file)}
                                                    onImageRemove={(index) => handleImageRemove('car_rear_image', index)}
                                                    accept="image/*"
                                                    required
                                                    slotWidth="200px"
                                                    slotHeight="150px"
                                                />
                                            </div>
                                        </div>

                                        {/* Nhóm ảnh bên trong xe */}
                                        <div className="mb-6">
                                            <h5 className="text-md font-medium mb-3 text-gray-700">Ảnh bên trong xe</h5>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <ImageSlotGrid
                                                    label="Ảnh ghế trước"
                                                    images={formData.car_front_seat_image ? [formData.car_front_seat_image] : []}
                                                    maxImages={1}
                                                    onImageAdd={(index, file) => handleImageAdd('car_front_seat_image', index, file)}
                                                    onImageRemove={(index) => handleImageRemove('car_front_seat_image', index)}
                                                    accept="image/*"
                                                    slotWidth="200px"
                                                    slotHeight="150px"
                                                />

                                                <ImageSlotGrid
                                                    label="Ảnh ghế sau"
                                                    images={formData.car_rear_seat_image ? [formData.car_rear_seat_image] : []}
                                                    maxImages={1}
                                                    onImageAdd={(index, file) => handleImageAdd('car_rear_seat_image', index, file)}
                                                    onImageRemove={(index) => handleImageRemove('car_rear_seat_image', index)}
                                                    accept="image/*"
                                                    slotWidth="200px"
                                                    slotHeight="150px"
                                                />
                                            </div>
                                        </div>
                                    </Panel>


                                    <Panel header="Giấy tờ phương tiện" className="mb-4" toggleable>
                                        {/* Nhóm giấy tờ chính */}

                                        <div className="mb-6">
                                            <h5 className="text-md font-medium mb-3 text-gray-700">Giấy tờ đăng ký và kiểm định { }</h5>
                                            <div className="grid grid-cols-1 gap-4">
                                                <ImageSlotGrid
                                                    label="Đăng ký xe / biên nhận thế chấp"
                                                    images={formData.vehicle_registration_urls || []}
                                                    maxImages={2}
                                                    onImageAdd={(index, file) => handleImageAdd('vehicle_registration_urls', index, file)}
                                                    onImageRemove={(index) => handleImageRemove('vehicle_registration_urls', index)}
                                                    accept="image/*,application/pdf"
                                                    required
                                                    description="Tối đa 2 ảnh, tối thiểu 1 ảnh"
                                                />

                                                <ImageSlotGrid
                                                    label="Giấy kiểm định"
                                                    images={formData.inspection_certificate_url || []}
                                                    maxImages={2}
                                                    onImageAdd={(index, file) => handleImageAdd('inspection_certificate_url', index, file)}
                                                    onImageRemove={(index) => handleImageRemove('inspection_certificate_url', index)}
                                                    accept="image/*,application/pdf"
                                                    required
                                                    description="2 ảnh"
                                                />
                                            </div>
                                        </div>

                                        {/* Nhóm phù hiệu và tem */}
                                        <div className="mb-6">
                                            <h5 className="text-md font-medium mb-3 text-gray-700">Phù hiệu và tem xe</h5>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <ImageSlotGrid
                                                    label="Phù hiệu xe hợp đồng"
                                                    images={formData.vehicle_badge_url || []}
                                                    maxImages={1}
                                                    onImageAdd={(index, file) => handleImageAdd('vehicle_badge_url', index, file)}
                                                    onImageRemove={(index) => handleImageRemove('vehicle_badge_url', index)}
                                                    accept="image/*"
                                                    description="Tối đa 1 ảnh"
                                                    slotWidth="200px"
                                                    slotHeight="150px"
                                                />

                                                <ImageSlotGrid
                                                    label="Tem phản quang"
                                                    images={formData.reflective_sticker_url ? [formData.reflective_sticker_url] : []}
                                                    maxImages={1}
                                                    onImageAdd={(index, file) => handleImageAdd('reflective_sticker_url', index, file)}
                                                    onImageRemove={(index) => handleImageRemove('reflective_sticker_url', index)}
                                                    accept="image/*"
                                                    description="1 ảnh"
                                                    slotWidth="200px"
                                                    slotHeight="150px"
                                                />
                                            </div>
                                        </div>

                                        {/* Nhóm bảo hiểm */}
                                        <div className="mb-6">
                                            <h5 className="text-md font-medium mb-3 text-gray-700">Bảo hiểm</h5>
                                            <ImageSlotGrid
                                                label="Bảo hiểm trách nhiệm dân sự"
                                                images={formData.insurance_certificate_url || []}
                                                maxImages={2}
                                                onImageAdd={(index, file) => handleImageAdd('insurance_certificate_url', index, file)}
                                                onImageRemove={(index) => handleImageRemove('insurance_certificate_url', index)}
                                                accept="image/*,application/pdf"
                                                required
                                                description="2 ảnh"
                                            />
                                        </div>
                                    </Panel>
                                </>
                            )}
                        </TabPanel>

                        {/* License Information Tab */}
                        <TabPanel header="Giấy phép lái xe" leftIcon="pi pi-book">
                            {formData.vehicle_type === 'motorbike' ? (
                                <>
                                    <Panel header="Thông tin GPLX xe máy" className="mb-4" toggleable>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Số GPLX</label>
                                                <InputText
                                                    value={formData.motorcycle_license_number}
                                                    onChange={(e) => handleInputChange('motorcycle_license_number', e.target.value)}
                                                    className="w-full"
                                                    placeholder="Nhập số GPLX"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-1">Số sê-ri GPLX</label>
                                                <InputText
                                                    value={formData.motorcycle_license_serial}
                                                    onChange={(e) => handleInputChange('motorcycle_license_serial', e.target.value)}
                                                    className="w-full"
                                                    placeholder="Nhập số sê-ri"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-1">Hạng GPLX</label>
                                                <Dropdown
                                                    value={formData.motorcycle_license_class}
                                                    options={licenseClassOptions}
                                                    onChange={(e) => handleInputChange('motorcycle_license_class', e.value)}
                                                    className="w-full"
                                                    placeholder="Chọn hạng GPLX"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-1">Ngày cấp GPLX</label>
                                                <Calendar
                                                    value={formData.motorcycle_license_issue_date}
                                                    onChange={(e) => handleInputChange('motorcycle_license_issue_date', e.value)}
                                                    className="w-full"
                                                    placeholder="Chọn ngày cấp"
                                                    dateFormat="dd/mm/yy"
                                                    showIcon
                                                />
                                            </div>
                                        </div>
                                    </Panel>

                                    <Panel header="Hình ảnh GPLX xe máy" className="mb-4" toggleable>
                                        <ImageSlotGrid
                                            label="Ảnh GPLX xe máy"
                                            images={formData.motorcycle_license_url || []}
                                            maxImages={2}
                                            onImageAdd={(index, file) => handleImageAdd('motorcycle_license_url', index, file)}
                                            onImageRemove={(index) => handleImageRemove('motorcycle_license_url', index)}
                                            accept="image/*,application/pdf"
                                            required
                                            description="1 ảnh mặt trước - 1 ảnh mặt sau"
                                        />
                                    </Panel>
                                </>
                            ) : (
                                <>
                                    <h4 className="text-lg font-semibold mb-4">Giấy phép lái xe ô tô</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Số GPLX</label>
                                            <InputText
                                                value={formData.car_license_number}
                                                onChange={(e) => handleInputChange('car_license_number', e.target.value)}
                                                className="w-full"
                                                placeholder="Nhập số GPLX"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-1">Số sê-ri GPLX</label>
                                            <InputText
                                                value={formData.car_license_serial}
                                                onChange={(e) => handleInputChange('car_license_serial', e.target.value)}
                                                className="w-full"
                                                placeholder="Nhập số sê-ri"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-1">Hạng GPLX</label>
                                            <Dropdown
                                                value={formData.car_license_class}
                                                options={licenseClassOptions}
                                                onChange={(e) => handleInputChange('car_license_class', e.value)}
                                                className="w-full"
                                                placeholder="Chọn hạng GPLX"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-1">Ngày cấp GPLX</label>
                                            <Calendar
                                                value={formData.car_license_selection_date}
                                                onChange={(e) => handleInputChange('car_license_selection_date', e.value)}
                                                className="w-full"
                                                placeholder="Chọn ngày cấp"
                                                dateFormat="dd/mm/yy"
                                                showIcon
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-1">Ngày hết hạn GPLX</label>
                                            <Calendar
                                                value={formData.car_license_expiry_date}
                                                onChange={(e) => handleInputChange('car_license_expiry_date', e.value)}
                                                className="w-full"
                                                placeholder="Chọn ngày hết hạn"
                                                dateFormat="dd/mm/yy"
                                                showIcon
                                            />
                                        </div>


                                    </div>

                                    <Divider />

                                    <h4 className="text-lg font-semibold mb-4">Hình ảnh GPLX ô tô</h4>
                                    <ImageSlotGrid
                                        label="Ảnh GPLX ô tô"
                                        images={formData.car_license_url || []}
                                        maxImages={2}
                                        onImageAdd={(index, file) => handleImageAdd('car_license_url', index, file)}
                                        onImageRemove={(index) => handleImageRemove('car_license_url', index)}
                                        accept="image/*,application/pdf"
                                        required
                                        description="2 ảnh GPLX"
                                    />
                                </>
                            )}
                        </TabPanel>

                        {/* Financial Information Tab */}
                        <TabPanel header="Thông tin tài chính" leftIcon="pi pi-money-bill">
                            <Panel header="Thông tin ngân hàng" className="mb-4" toggleable>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Tên ngân hàng</label>
                                        <InputText
                                            value={formData.bank_name}
                                            onChange={(e) => handleInputChange('bank_name', e.target.value)}
                                            className="w-full"
                                            placeholder="Nhập tên ngân hàng"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Tên chủ tài khoản</label>
                                        <InputText
                                            value={formData.account_holder_name}
                                            onChange={(e) => handleInputChange('account_holder_name', e.target.value)}
                                            className="w-full"
                                            placeholder="Nhập tên chủ tài khoản"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Số tài khoản</label>
                                        <InputText
                                            value={formData.account_number}
                                            onChange={(e) => handleInputChange('account_number', e.target.value)}
                                            className="w-full"
                                            placeholder="Nhập số tài khoản"
                                        />
                                    </div>
                                </div>
                            </Panel>

                            <Panel header="Thông tin thuế" className="mb-4" toggleable>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Mã số thuế</label>
                                        <InputText
                                            value={formData.tax_id}
                                            onChange={(e) => handleInputChange('tax_id', e.target.value)}
                                            className="w-full"
                                            placeholder="Nhập mã số thuế"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Nơi cấp mã số thuế</label>
                                        <InputText
                                            value={formData.tax_issue_place}
                                            onChange={(e) => handleInputChange('tax_issue_place', e.target.value)}
                                            className="w-full"
                                            placeholder="Nhập nơi cấp"
                                        />
                                    </div>
                                </div>
                            </Panel>
                        </TabPanel>

                        {/* Emergency Contact Tab */}
                        <TabPanel header="Liên hệ khẩn cấp" leftIcon="pi pi-phone">
                            <h4 className="text-lg font-semibold mb-4">Thông tin liên hệ khẩn cấp</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Họ tên người liên hệ</label>
                                    <InputText
                                        value={formData.emergency_contact_name}
                                        onChange={(e) => handleInputChange('emergency_contact_name', e.target.value)}
                                        className="w-full"
                                        placeholder="Nhập họ tên"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Số điện thoại liên hệ</label>
                                    <InputText
                                        value={formData.emergency_contact_phone}
                                        onChange={(e) => handleInputChange('emergency_contact_phone', e.target.value)}
                                        className="w-full"
                                        placeholder="Nhập số điện thoại"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Mối quan hệ</label>
                                    <Dropdown
                                        value={formData.emergency_contact_relationship}
                                        options={relationshipOptions}
                                        onChange={(e) => handleInputChange('emergency_contact_relationship', e.value)}
                                        className="w-full"
                                        placeholder="Chọn mối quan hệ"
                                    />
                                </div>
                            </div>
                        </TabPanel>

                        {/* Documents Tab */}
                        <TabPanel header="Giấy tờ khác" leftIcon="pi pi-file">
                            <div className="space-y-6">
                                <h4 className="text-lg font-semibold mb-4">Giấy tờ sức khỏe</h4>
                                <div className="grid grid-cols-1 gap-4">
                                    <div className="relative">
                                        <ImageSlotGrid
                                            label="Giấy khám sức khỏe"
                                            images={formData.health_certificate_urls || []}
                                            maxImages={4}
                                            onImageAdd={(index, file) => handleImageAdd('health_certificate_urls', index, file)}
                                            onImageRemove={(index) => handleImageRemove('health_certificate_urls', index)}
                                            accept="image/*,application/pdf"
                                            description="Tối đa 4 ảnh, tối thiểu 1 ảnh"
                                            disabled={isFieldUploading('health_certificate_urls')}
                                        />
                                        {(isFieldUploading('health_certificate_urls', 0) || isFieldUploading('health_certificate_urls', 1) ||
                                            isFieldUploading('health_certificate_urls', 2) || isFieldUploading('health_certificate_urls', 3)) && (
                                                <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-lg">
                                                    <div className="flex flex-col items-center space-y-2">
                                                        <i className="pi pi-spin pi-spinner text-blue-500 text-xl"></i>
                                                        <span className="text-sm text-gray-600">Đang tải giấy tờ sức khỏe...</span>
                                                    </div>
                                                </div>
                                            )}
                                    </div>

                                    <div className="relative">
                                        <ImageSlotGrid
                                            label="Xét nghiệm ma túy"
                                            images={formData.drug_test_urls || []}
                                            maxImages={2}
                                            onImageAdd={(index, file) => handleImageAdd('drug_test_urls', index, file)}
                                            onImageRemove={(index) => handleImageRemove('drug_test_urls', index)}
                                            accept="image/*,application/pdf"
                                            description="Tối đa 2 ảnh, tối thiểu 1 ảnh"
                                            disabled={isFieldUploading('drug_test_urls')}
                                        />
                                        {(isFieldUploading('drug_test_urls', 0) || isFieldUploading('drug_test_urls', 1)) && (
                                            <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-lg">
                                                <div className="flex flex-col items-center space-y-2">
                                                    <i className="pi pi-spin pi-spinner text-blue-500 text-xl"></i>
                                                    <span className="text-sm text-gray-600">Đang tải kết quả xét nghiệm...</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <Divider />

                                <h4 className="text-lg font-semibold mb-4">Giấy tờ pháp lý</h4>
                                <div className="grid grid-cols-1 gap-4">
                                    <ImageSlotGrid
                                        label="Lý lịch tư pháp"
                                        images={formData.criminal_record_urls || []}
                                        maxImages={1}
                                        onImageAdd={(index, file) => handleImageAdd('criminal_record_urls', index, file)}
                                        onImageRemove={(index) => handleImageRemove('criminal_record_urls', index)}
                                        accept="image/*,application/pdf"
                                        description="1 ảnh"
                                    />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <ImageSlotGrid
                                            label="Video cam kết"
                                            images={formData.commitment_video_url ? [formData.commitment_video_url] : []}
                                            maxImages={1}
                                            onImageAdd={(index, file) => handleImageAdd('commitment_video_url', index, file)}
                                            onImageRemove={(index) => handleImageRemove('commitment_video_url', index)}
                                            accept="video/*"
                                            description="1 video"
                                            slotWidth="200px"
                                            slotHeight="150px"
                                        />

                                        <ImageSlotGrid
                                            label="Chữ ký"
                                            images={formData.signature_url ? [formData.signature_url] : []}
                                            maxImages={1}
                                            onImageAdd={(index, file) => handleImageAdd('signature_url', index, file)}
                                            onImageRemove={(index) => handleImageRemove('signature_url', index)}
                                            accept="image/*"
                                            description="1 ảnh"
                                            slotWidth="200px"
                                            slotHeight="150px"
                                        />
                                    </div>

                                    {formData.vehicle_type === 'car' && (
                                        <ImageSlotGrid
                                            label="Thành viên hợp tác xã"
                                            images={formData.cooperative_member_urls || []}
                                            maxImages={4}
                                            onImageAdd={(index, file) => handleImageAdd('cooperative_member_urls', index, file)}
                                            onImageRemove={(index) => handleImageRemove('cooperative_member_urls', index)}
                                            accept="image/*,application/pdf"
                                            description="Tối đa 4 ảnh"
                                        />
                                    )}
                                </div>
                            </div>
                        </TabPanel>
                    </TabView>
                </div>
            </Dialog>
        </>
    );
};
